import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ModalInMobile, PrimaryButton, H1 } from '../../components';
import SingleDatePicker from '../DatePicker/DatePickers/SingleDatePicker';
import AddOnsSelector, { buildActiveAddons } from '../AddOnsSelector/AddOnsSelector';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import ServiceCard from './ServiceCard';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';

import { PICKUP_OPTION_LABELS, APPROVED_DESTINATION_LABELS } from '../../util/fulfillmentHelpers';
import { truncateAddressToCity } from '../../util/truncateAddressToCity';
import { computeAgeGateResult } from '../../util/ageVerification';
import { NamedLink } from '../../components';

import css from './OrderPanel.module.css';

// Push 10 Phase D.5 — render-side adapter over the shared
// computeAgeGateResult helper. Returns a FormattedMessage node when the
// gate fires (DOB missing OR age below minimum_renter_age for a
// logged-in customer), or null when it passes. Reuses Phase D's
// CheckoutPage.* locale keys for wording consistency between this
// client gate and the server-side error display.
//
// Push 10 Phase E.5 — threads ICU `<link>` placeholder values for the
// `CheckoutPage.dobMissing` message so the customer gets a clickable
// link to the profile-settings page where they can add a DOB. Mirrors
// the same `dobMissingLinkValues` usage in CheckoutPage/ErrorMessages.js.
const computeAgeGateMessage = (currentUser, listing) => {
  const result = computeAgeGateResult(currentUser, listing);
  if (!result.blocked) return null;
  if (result.reason === 'dob_missing') {
    return (
      <FormattedMessage
        id="CheckoutPage.dobMissing"
        values={{
          // Push 10 Phase E.5 cleanup — link routes to ManageAccountPage
          // (/account/manage), not ProfileSettingsPage. The DOB userField
          // (scope:'protected') renders on the Manage Account page per
          // Sharetribe v10.3.0+ convention.
          link: chunks => <NamedLink name="ManageAccountPage">{chunks}</NamedLink>,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="CheckoutPage.ageRequirementNotMet"
      values={{ minimumAge: result.minimumAge }}
    />
  );
};

const { Money } = sdkTypes;

const DELIVERY_OPTIONS_REQUIRING_SECTION = ['provider-delivers', 'third-party-ok', 'customer-tow-away'];


/**
 * Pickup / Delivery section shown in the booking panel when the listing
 * offers non-home-base pickup options.
 */
const DeliverySection = props => {
  const {
    pickupOptions,
    approvedDestinations,
    deliveryZones,
    deliveryOption,
    setDeliveryOption,
    deliveryZone,
    setDeliveryZone,
    setDeliveryZoneFee,
    deliveryLocation,
    setDeliveryLocation,
    intl,
  } = props;

  const CANONICAL_ORDER = Object.keys(PICKUP_OPTION_LABELS);
  const sortedOptions = pickupOptions
    .slice()
    .sort((a, b) => CANONICAL_ORDER.indexOf(a) - CANONICAL_ORDER.indexOf(b));

  const showZoneDropdown = deliveryOption === 'provider-delivers' && deliveryZones.length > 0;
  const showRequestedLocation = deliveryOption === 'provider-delivers';
  const showPickupAddress = deliveryOption === 'customer-tow-away';

  const destinationLabels = (approvedDestinations || [])
    .map(d => APPROVED_DESTINATION_LABELS[d] || d)
    .join(', ');

  const handleZoneChange = e => {
    const selectedLabel = e.target.value;
    const zone = deliveryZones.find(z => z.label === selectedLabel);
    setDeliveryZone(selectedLabel);
    setDeliveryZoneFee(zone ? zone.fee : 0);
  };

  const OPTION_CARD_INFO = {
    'board-at-home-base': {
      label: 'Board at Home Base',
      description: 'Meet us at the marina',
      price: 'Free',
    },
    'customer-pickup': {
      label: 'Customer Pickup',
      description: 'Pick up at the marina',
      price: 'Free',
    },
    'customer-tow-away': {
      label: 'Self Tow',
      description: 'Tow the boat yourself',
      price: 'Free',
    },
    'provider-delivers': {
      label: 'Provider Delivery',
      description: 'We bring the boat to you',
      price:
        deliveryZones.length === 1
          ? `+${intl.formatNumber(deliveryZones[0].fee / 100, { style: 'currency', currency: 'USD' })}`
          : deliveryZones.length > 1
          ? 'Price varies by zone'
          : 'Contact provider',
    },
    'third-party-ok': {
      label: '3rd Party Delivery',
      description: 'Arrange with a transport provider',
      price: 'Handled after booking',
    },
  };

  // Only board-at-home-base (or no options) — show compact info line
  if (
    sortedOptions.length <= 1 &&
    (sortedOptions.length === 0 || sortedOptions[0] === 'board-at-home-base')
  ) {
    return (
      <div className={css.deliveryCardsSection}>
        <p className={css.deliveryCardsSectionLabel}>Pickup or Delivery?</p>
        <p className={css.deliveryInfoLine}>📍 Board at Home Base · Meet at the marina</p>
      </div>
    );
  }

  return (
    <div className={css.deliveryCardsSection}>
      <p className={css.deliveryCardsSectionLabel}>Pickup or Delivery?</p>
      <div className={css.deliveryCards}>
        {sortedOptions.map(opt => {
          const info = OPTION_CARD_INFO[opt] || {
            label: PICKUP_OPTION_LABELS[opt] || opt,
            description: '',
            price: '',
          };
          const isSelected = deliveryOption === opt;
          return (
            <div
              key={opt}
              className={`${css.deliveryCard} ${isSelected ? css.deliveryCardSelected : ''}`}
              onClick={() => setDeliveryOption(opt)}
            >
              <div className={css.deliveryCardLeft}>
                <span className={css.deliveryCardName}>{info.label}</span>
                {info.description ? (
                  <span className={css.deliveryCardDesc}>{info.description}</span>
                ) : null}
              </div>
              {info.price ? (
                <span className={css.deliveryCardPrice}>{info.price}</span>
              ) : null}
            </div>
          );
        })}
      </div>

      {sortedOptions.includes('third-party-ok') ? (
        <p className={css.thirdPartyMicrocopy}>
          We'll recommend trusted delivery drivers after booking.
        </p>
      ) : null}

      {showZoneDropdown ? (
        <div className={css.deliveryField}>
          <label className={css.deliveryLabel} htmlFor="deliveryZone">
            Select a delivery zone
          </label>
          <select
            id="deliveryZone"
            className={css.deliverySelect}
            value={deliveryZone}
            onChange={handleZoneChange}
          >
            <option value="">Select a zone…</option>
            {deliveryZones.map(z => (
              <option key={z.label} value={z.label}>
                {z.label} —{' '}
                {intl.formatNumber(z.fee / 100, { style: 'currency', currency: 'USD' })}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {showRequestedLocation && destinationLabels ? (
        <p className={css.deliveryHelperText}>Provider delivers to: {destinationLabels}</p>
      ) : null}

      {showPickupAddress ? (
        <div className={css.deliveryField}>
          <label className={css.deliveryLabel} htmlFor="deliveryLocationPickup">
            Pickup Address
          </label>
          <input
            id="deliveryLocationPickup"
            type="text"
            className={css.deliveryInput}
            value={deliveryLocation}
            onChange={e => setDeliveryLocation(e.target.value)}
            placeholder="Pickup address"
          />
        </div>
      ) : null}

    </div>
  );
};

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;

const FEE_PERCENT = parseFloat(process.env.REACT_APP_DEFAULT_CUSTOMER_FEE_PERCENT) || 8.5;
const MINIMUM_FEE = parseInt(process.env.REACT_APP_MINIMUM_CUSTOMER_FEE, 10) || 0;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

// Format money without trailing .00 for whole dollar amounts.
const formatMoneyNoCents = (price, intl) => {
  if (!price?.amount) return null;
  const isWholeDollar = price.amount % 100 === 0;
  try {
    return intl.formatNumber(price.amount / 100, {
      style: 'currency',
      currency: price.currency,
      maximumFractionDigits: isWholeDollar ? 0 : 2,
    });
  } catch (e) {
    return formatMoneyIfSupportedCurrency(price, intl);
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    categoryLevel1,
    serviceMenu,
  } = props;
  const { listingType, unitType } = publicData || {};

  // Dock-ramp-delivery: skip the "Starting at $X" header entirely. Service-tier
  // pricing is shown on each card in the widget; the estimated total at the
  // bottom of the widget includes "✓ No hidden fees" inline. Top header
  // removed for vertical compactness so the Request Service CTA stays above
  // the fold on typical viewports.
  if (categoryLevel1 === 'dock-ramp-delivery') {
    return null;
  }

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Use best hourly rate from rental slots if available.
  // For accessory-rental with slots, use the cheapest slot price directly (not a rate).
  const rentalSlots = publicData?.rental_slots;
  const hasPricingFromSlots = Array.isArray(rentalSlots) && rentalSlots.length > 0;
  const isAccessoryRental = listingType === 'accessory-rental';
  const basePrice = (() => {
    if (hasPricingFromSlots && price) {
      if (isAccessoryRental) {
        // Show starting slot price (not per-hour rate) for accessory rentals
        const lowestSlotPrice = Math.min(...rentalSlots.map(s => s.price));
        return new Money(lowestSlotPrice, price.currency);
      }
      const bestRateCents = Math.min(...rentalSlots.map(s => s.price / s.hours));
      return new Money(Math.ceil(bestRateCents / 100) * 100, price.currency);
    }
    return price;
  })();

  // Roll the customer fee into the displayed price (all-in price per SB 478).
  // Round up to the nearest whole dollar so the formatter never shows cents.
  const allInPrice = (() => {
    if (basePrice?.amount == null) return basePrice;
    const calculatedFee = Math.round(basePrice.amount * (FEE_PERCENT / 100));
    const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
    const raw = basePrice.amount + actualFee;
    return new Money(Math.ceil(raw / 100) * 100, basePrice.currency);
  })();

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(allInPrice, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>
      {formatMoneyNoCents(allInPrice, intl)}
      {hasPricingFromSlots ? '+' : ''}
    </span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      {listingType === 'captained-charter-ticketed'
        ? 'per person'
        : isAccessoryRental && hasPricingFromSlots
        ? null
        : <FormattedMessage id="OrderPanel.perHour" />}
    </span>
  );

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  const minimumHours = publicData?.minimum_hours;
  const experienceSummary = publicData?.experience_summary || null;

  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div>
      <div className={css.priceRow}>
        <div className={css.priceLeft}>
          <p className={css.price}>
            <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
          </p>
        </div>
        <div className={css.priceRight}>
          {minimumHours > 1 ? (
            <p className={css.minimumHours}>
              <FormattedMessage id="OrderPanel.minimumHours" values={{ minimumHours }} />
            </p>
          ) : null}
          <p className={css.noHiddenFees}>✓ No hidden fees</p>
        </div>
      </div>
      {experienceSummary ? (
        <p className={css.experienceSummary}>{experienceSummary}</p>
      ) : null}
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

// Half-hour time slots from 6:00 AM to 9:00 PM
const DOCK_DELIVERY_TIMES = Array.from({ length: 31 }, (_, i) => {
  const totalMinutes = 6 * 60 + i * 30;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const ampm = hours < 12 ? 'AM' : 'PM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const label = `${displayHours}:${mins === 0 ? '00' : '30'} ${ampm}`;
  const value = `${String(hours).padStart(2, '0')}:${mins === 0 ? '00' : '30'}`;
  return { label, value };
});

const DOCK_WIDGET_SESSION_KEY = 'dockDeliveryWidgetState';

const DockRampDeliveryWidget = ({
  serviceMenu,
  onSubmit,
  onFetchTransactionLineItems,
  listingId,
  isOwnListing,
  lineItems,
  fetchLineItemsInProgress,
  isInstantBook,
  intl,
  marketplaceCurrency,
  price,
  addOns,
  selectedAddons,
  setSelectedAddons,
}) => {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [selectedDateObj, setSelectedDateObj] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [roundTripSelected, setRoundTripSelected] = useState(false);
  const [returnDateObj, setReturnDateObj] = useState(null);
  const [returnPickupTime, setReturnPickupTime] = useState('09:00');
  // Clear sessionStorage when the widget unmounts — covers both successful submit
  // (navigated to checkout) and the user navigating away without completing a booking.
  useEffect(() => {
    return () => {
      try { sessionStorage.removeItem(DOCK_WIDGET_SESSION_KEY); } catch (e) {}
    };
  }, []); // eslint-disable-line

  const selectedService = selectedIdx !== null ? serviceMenu[selectedIdx] : null;
  const currency = price?.currency || marketplaceCurrency;

  // Fire a line-items fetch whenever pricing-relevant state changes. The parent's
  // onFetchTransactionLineItemsWithDelivery wrapper injects selectedAddons and
  // sets lastLineItemsParamsRef.current — both of which are needed for parent's
  // useEffect-on-selectedAddons to refetch on addon toggles. Without this seed
  // call the widget would fall back to a local price estimate that has no
  // awareness of addons.
  useEffect(() => {
    if (!selectedService || !selectedDateObj) return;
    if (typeof onFetchTransactionLineItems !== 'function') return;

    const [hh, mm] = selectedTime.split(':');
    const deliveryDateLocal = new Date(
      selectedDateObj.getFullYear(),
      selectedDateObj.getMonth(),
      selectedDateObj.getDate(),
      parseInt(hh, 10),
      parseInt(mm, 10),
      0,
      0
    );
    if (isNaN(deliveryDateLocal.getTime())) return;

    const deliveredByMs = deliveryDateLocal.getTime();
    const deliveryDateStr = `${selectedDateObj.getFullYear()}-${String(selectedDateObj.getMonth() + 1).padStart(2, '0')}-${String(selectedDateObj.getDate()).padStart(2, '0')}`;

    const orderData = {
      bookingStart: new Date(deliveredByMs - 3600000),
      bookingEnd: new Date(deliveredByMs + 3600000),
      selectedServicePrice: selectedService.priceInCents,
      selectedServiceName: selectedService.title || selectedService.name,
      deliveryDate: deliveryDateStr,
      deliveredByTime: selectedTime,
    };

    if (roundTripSelected && returnDateObj && returnPickupTime) {
      const returnDateStr = `${returnDateObj.getFullYear()}-${String(returnDateObj.getMonth() + 1).padStart(2, '0')}-${String(returnDateObj.getDate()).padStart(2, '0')}`;
      orderData.roundTrip = true;
      orderData.returnDate = returnDateStr;
      orderData.returnPickupTime = returnPickupTime;
    }

    onFetchTransactionLineItems({
      orderData,
      listingId,
      isOwnListing,
    });
    // Intentionally omit onFetchTransactionLineItems, listingId, isOwnListing.
    // The wrapper isn't memoized so its identity churns on every parent render —
    // including it caused an infinite refetch loop. listingId/isOwnListing don't
    // change in ways this effect needs to react to (navigation unmounts; auth
    // state change is rare and the form-state deps will naturally re-fire).
  }, [
    selectedService,
    selectedDateObj,
    selectedTime,
    roundTripSelected,
    returnDateObj,
    returnPickupTime,
  ]);

  const formatPrice = cents =>
    intl.formatNumber(cents / 100, {
      style: 'currency',
      currency,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const isDayBlocked = day => day < todayStart;
  // Return-trip picker: also disallow dates earlier than the selected delivery date.
  const isReturnDayBlocked = day => {
    if (day < todayStart) return true;
    if (!selectedDateObj) return false;
    const deliveryStart = new Date(
      selectedDateObj.getFullYear(),
      selectedDateObj.getMonth(),
      selectedDateObj.getDate()
    );
    return day < deliveryStart;
  };

  const canSubmit =
    !!selectedService &&
    !!selectedDateObj &&
    !!selectedTime &&
    (!roundTripSelected || (!!returnDateObj && !!returnPickupTime));

  const handleSubmit = () => {
    if (!selectedService || !selectedDateObj) return;
    // Build a full Date by combining the selected calendar date (year/month/day in local time)
    // with the selected delivery time (24h HH:MM string). Using getFullYear/getMonth/getDate
    // avoids any UTC-offset shift that would happen with selectedDateObj.getTime() directly.
    const [hh, mm] = selectedTime.split(':');
    const hhInt = parseInt(hh, 10);
    const mmInt = parseInt(mm, 10);
    const deliveryDateLocal = new Date(
      selectedDateObj.getFullYear(),
      selectedDateObj.getMonth(),
      selectedDateObj.getDate(),
      hhInt,
      mmInt,
      0,
      0
    );
    if (isNaN(deliveryDateLocal.getTime())) return;
    const deliveredByMs = deliveryDateLocal.getTime();
    const bookingStartMs = deliveredByMs - 3600000;
    const bookingEndMs = deliveredByMs + 3600000;
    // Sharetribe booking window: 1hr before to 1hr after delivery time (never spans multiple days)
    const deliveryDateStr = `${selectedDateObj.getFullYear()}-${String(selectedDateObj.getMonth() + 1).padStart(2, '0')}-${String(selectedDateObj.getDate()).padStart(2, '0')}`;
    const submitData = {
      bookingStartTime: bookingStartMs.toString(),
      bookingEndTime: bookingEndMs.toString(),
      deliveredByTime: selectedTime,
      deliveryDate: deliveryDateStr,
      selectedServicePrice: selectedService.priceInCents,
      selectedServiceName: selectedService.name,
    };
    if (roundTripSelected && returnDateObj) {
      const returnDate = new Date(returnDateObj.getTime());
      const [rhh, rmm] = returnPickupTime.split(':');
      returnDate.setHours(parseInt(rhh, 10), parseInt(rmm, 10), 0, 0);
      if (!isNaN(returnDate.getTime())) {
        // bookingEndTime stays as deliveredByTime + 1hr — return trip stored in protectedData only
        submitData.returnDate = `${returnDateObj.getFullYear()}-${String(returnDateObj.getMonth() + 1).padStart(2, '0')}-${String(returnDateObj.getDate()).padStart(2, '0')}`;
        submitData.returnPickupTime = returnPickupTime;
        submitData.roundTrip = true;
      }
    }
    // Navigate to checkout unconditionally — the line items prefetch is only for
    // the estimated total display. The actual calculation happens server-side.
    // Do NOT gate this on fetchLineItemsError or fetchLineItemsInProgress.
    onSubmit(submitData);
  };

  const customerItems = Array.isArray(lineItems)
    ? lineItems.filter(item => item.includeFor?.includes('customer') && item.quantity !== 0)
    : [];
  const estimatedTotal =
    customerItems.length > 0 && !fetchLineItemsInProgress
      ? customerItems.reduce((sum, item) => {
          const lt =
            item.lineTotal?.amount ??
            item.unitPrice?.amount *
              (item.quantity ?? (item.units != null ? item.units * item.seats : 1));
          return sum + (lt || 0);
        }, 0)
      : null;

  if (!serviceMenu || serviceMenu.length === 0) {
    return (
      <div className={css.deliveryWidget}>
        <p className={css.deliveryWidgetEmpty}>No services available for this listing.</p>
      </div>
    );
  }

  return (
    <div className={css.deliveryWidget}>
      <p className={css.deliveryWidgetSectionLabel}>Select a Service</p>
      <div className={css.deliveryServiceCards}>
        {serviceMenu.map((service, idx) => {
          // Backward compat: support both new (title) and legacy (name) fields
          const cardTitle = service.title || service.name || '';
          const cardSubtext = service.subtext || '';
          return (
            <ServiceCard
              key={idx}
              idx={idx}
              title={cardTitle}
              subtext={cardSubtext}
              price={service.priceInCents}
              selected={selectedIdx === idx}
              formatPrice={formatPrice}
              onSelect={setSelectedIdx}
            />
          );
        })}
      </div>

      <div className={css.deliveryDateRow}>
        <div className={css.deliveryDateGroup}>
          <label className={css.deliveryFieldLabel}>Delivery Date</label>
          <SingleDatePicker
            id={`dockDeliveryDate_${listingId}`}
            placeholderText="Date"
            isDayBlocked={isDayBlocked}
            value={selectedDateObj}
            onChange={date => setSelectedDateObj(date)}
            inputClassName={css.deliveryDatePickerInputWrapper}
            popupClassName={css.deliveryDatePickerPopup}
            openDirection="up"
          />
        </div>
        <div className={css.deliveryDateGroup}>
          <label className={css.deliveryFieldLabel}>Delivery Time</label>
          <select
            className={css.deliveryTimeSelect}
            value={selectedTime}
            onChange={e => setSelectedTime(e.target.value)}
          >
            {DOCK_DELIVERY_TIMES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className={css.deliveryRoundTripInline}>
          <label htmlFor={`roundTrip_${listingId}`} className={css.deliveryRoundTripLabel}>
            Round-trip?
          </label>
          <input
            type="checkbox"
            id={`roundTrip_${listingId}`}
            className={css.deliveryRoundTripCheckbox}
            checked={roundTripSelected}
            onChange={e => {
              const checked = e.target.checked;
              setRoundTripSelected(checked);
              // Pre-populate return date with the selected delivery date so the user
              // only has to override it if they actually want a different return day.
              if (checked && selectedDateObj && !returnDateObj) {
                setReturnDateObj(selectedDateObj);
              }
            }}
          />
        </div>
      </div>

      {roundTripSelected && (
        <div className={css.deliveryDateRow}>
          <div className={css.deliveryDateGroup}>
            <label className={css.deliveryFieldLabel}>Pickup Date</label>
            <SingleDatePicker
              id={`dockReturnDate_${listingId}`}
              placeholderText="Date"
              isDayBlocked={isReturnDayBlocked}
              value={returnDateObj}
              onChange={date => setReturnDateObj(date)}
              inputClassName={css.deliveryDatePickerInputWrapper}
              popupClassName={css.deliveryDatePickerPopup}
              openDirection="up"
            />
          </div>
          <div className={css.deliveryDateGroup}>
            <label className={css.deliveryFieldLabel}>Pickup Time</label>
            <select
              className={css.deliveryTimeSelect}
              value={returnPickupTime}
              onChange={e => setReturnPickupTime(e.target.value)}
            >
              {DOCK_DELIVERY_TIMES.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {Array.isArray(addOns) && addOns.length > 0 ? (
        <div style={{ marginTop: '12px' }}>
          <AddOnsSelector
            addOns={addOns}
            selectedAddons={selectedAddons}
            setSelectedAddons={setSelectedAddons}
          />
        </div>
      ) : null}

      {(() => {
        // If line items have returned, use the actual total from the server.
        if (estimatedTotal != null && !fetchLineItemsInProgress) {
          const isWhole = estimatedTotal % 100 === 0;
          const formatted = intl.formatNumber(estimatedTotal / 100, {
            style: 'currency',
            currency,
            maximumFractionDigits: isWhole ? 0 : 2,
          });
          return (
            <div className={css.estimatedTotal} style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div className={css.estimatedTotalRow}>
                <span className={css.estimatedTotalAmount}>Est. {formatted}</span>
                <span className={css.noHiddenFees}>✓ No hidden fees</span>
              </div>
              <p className={css.estimatedTotalNote}>Includes fees. Extras may change final price.</p>
            </div>
          );
        }

        // Compute a local estimate from the selected (or cheapest) service + fee.
        const baseService = selectedService || serviceMenu.reduce((cheapest, s) =>
          s.priceInCents < cheapest.priceInCents ? s : cheapest
        , serviceMenu[0]);

        if (baseService) {
          const calculatedFee = Math.round(baseService.priceInCents * (FEE_PERCENT / 100));
          const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
          const rawTotal = baseService.priceInCents + actualFee;
          const totalCents = Math.ceil(rawTotal / 100) * 100;
          const isWhole = totalCents % 100 === 0;
          const formatted = intl.formatNumber(totalCents / 100, {
            style: 'currency',
            currency,
            maximumFractionDigits: isWhole ? 0 : 2,
          });
          return (
            <div className={css.estimatedTotal} style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div className={css.estimatedTotalRow}>
                <span className={css.estimatedTotalAmount}>Est. {formatted}</span>
                <span className={css.noHiddenFees}>✓ No hidden fees</span>
              </div>
              <p className={css.estimatedTotalNote}>
                {selectedService ? 'Includes fees. Extras may change final price.' : 'Based on your selection'}
              </p>
            </div>
          );
        }

        return null;
      })()}

      <PrimaryButton
        type="button"
        className={css.deliverySubmitBtn}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {isInstantBook ? 'Book Service' : 'Request Service'}
      </PrimaryButton>
    </div>
  );
};

// ─── Trip type selector ────────────────────────────────────────────────────
const TripTypeSelector = ({ tripType, setTripType, onReset }) => (
  <div className={css.tripTypeSelector}>
    <button
      type="button"
      className={tripType === 'hourly' ? css.tripTypePillActive : css.tripTypePill}
      onClick={() => { setTripType('hourly'); onReset && onReset(); }}
    >
      ☀️ Hourly Rentals
    </button>
    <button
      type="button"
      className={tripType === 'multiday' ? css.tripTypePillActive : css.tripTypePill}
      onClick={() => { setTripType('multiday'); onReset && onReset(); }}
    >
      📅 Multi-Day Rentals
    </button>
  </div>
);

// Default hourly time options (6 AM – 10 PM) used when no availability plan entries exist
const MULTI_DAY_DEFAULT_TIMES = Array.from({ length: 17 }, (_, i) => {
  const h = i + 6;
  const ampm = h < 12 ? 'AM' : 'PM';
  const displayH = h > 12 ? h - 12 : h;
  return { label: `${displayH}:00 ${ampm}`, value: `${String(h).padStart(2, '0')}:00` };
});

// ─── Multi-Day booking widget ──────────────────────────────────────────────
const MultiDayBookingWidget = ({
  multiDaySlots,
  availabilityPlan,
  timeZone,
  onSubmit,
  isInstantBook,
  price,
  marketplaceCurrency,
  intl,
  quantityStepperNode,
  gateErrorNode, // Push 10 Phase D.5 — optional age-gate message rendered above the Continue button
}) => {
  const [selectedSlotValue, setSelectedSlotValue] = useState('');
  const [selectedDateObj, setSelectedDateObj] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState('');

  const selectedSlot = multiDaySlots.find(s => String(s.value || s.days) === selectedSlotValue) || null;

  // Generate 1-hour time options from the availability plan entries for a given date
  const getAvailableTimesForDate = dateObj => {
    if (!availabilityPlan || !dateObj) return MULTI_DAY_DEFAULT_TIMES;
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dow = days[dateObj.getDay()];
    const entries = (availabilityPlan.entries || []).filter(e => e.dayOfWeek === dow);
    if (entries.length === 0) return MULTI_DAY_DEFAULT_TIMES;
    const times = [];
    entries.forEach(entry => {
      const [sh, sm] = entry.startTime.split(':').map(Number);
      const [eh, em] = entry.endTime.split(':').map(Number);
      const endMinutes = eh === 0 && em === 0 ? 24 * 60 : eh * 60 + em;
      for (let m = sh * 60 + sm; m < endMinutes; m += 60) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        const ampm = h < 12 ? 'AM' : 'PM';
        const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const label = `${displayH}:${min === 0 ? '00' : String(min).padStart(2, '0')} ${ampm}`;
        const value = `${String(h).padStart(2, '0')}:${min === 0 ? '00' : String(min).padStart(2, '0')}`;
        times.push({ label, value });
      }
    });
    return times;
  };

  // Format {h, m} as a display label e.g. "5:00 PM"
  const fmtHM = ({ h, m }) => {
    const ampm = h < 12 ? 'AM' : 'PM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${m === 0 ? '00' : String(m).padStart(2, '0')} ${ampm}`;
  };

  // Get provider's closing time minus 2 hours on the return date (the booking end time)
  const getBookingEndTimeHM = dateObj => {
    if (!availabilityPlan || !dateObj) return { h: 17, m: 0 };
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dow = days[dateObj.getDay()];
    const entries = (availabilityPlan.entries || []).filter(e => e.dayOfWeek === dow);
    if (entries.length === 0) return { h: 17, m: 0 };
    const lastEntry = entries[entries.length - 1];
    const [eh, em] = lastEntry.endTime.split(':').map(Number);
    // "00:00" endTime means midnight — treat as 22:00 (midnight minus 2 hours)
    const closingTotalMins = (eh === 0 && em === 0) ? 24 * 60 : eh * 60 + em;
    const endTotalMins = Math.max(closingTotalMins - 120, 0);
    return { h: Math.floor(endTotalMins / 60), m: endTotalMins % 60 };
  };

  // Get provider's closing time label for display (e.g. "Returns by 5:00 PM")
  const getClosingTimeLabel = dateObj => fmtHM(getBookingEndTimeHM(dateObj));

  // Get provider's earliest start time for the given date
  const getEarliestStartTimeHM = dateObj => {
    if (!availabilityPlan || !dateObj) return { h: 9, m: 0 };
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dow = days[dateObj.getDay()];
    const entries = (availabilityPlan.entries || []).filter(e => e.dayOfWeek === dow);
    if (entries.length === 0) return { h: 9, m: 0 };
    const [sh, sm] = entries[0].startTime.split(':').map(Number);
    return { h: sh, m: sm };
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const isDayBlocked = day => day < todayStart;

  const availableTimes = selectedDateObj
    ? getAvailableTimesForDate(selectedDateObj).length > 0
      ? getAvailableTimesForDate(selectedDateObj)
      : MULTI_DAY_DEFAULT_TIMES
    : MULTI_DAY_DEFAULT_TIMES;

  const endDateObj =
    selectedDateObj && selectedSlot
      ? new Date(selectedDateObj.getTime() + selectedSlot.days * 24 * 60 * 60 * 1000)
      : null;

  const currency = price?.currency || marketplaceCurrency;
  const fmtPrice = cents =>
    intl.formatNumber(cents / 100, {
      style: 'currency',
      currency,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    });
  const fmtDate = dateObj =>
    dateObj
      ? intl.formatDate(dateObj, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: timeZone || undefined,
        })
      : '';

  const priceBreakdown = (() => {
    if (!selectedSlot) return null;
    const basePrice = selectedSlot.price;
    const perDay = selectedSlot.pricePerDay ?? basePrice;
    const calculatedFee = Math.round(basePrice * (FEE_PERCENT / 100));
    const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
    return { basePrice, perDay, fee: actualFee, total: basePrice + actualFee };
  })();

  const canContinue = !!selectedSlot && !!selectedDateObj;

  const handleContinue = () => {
    if (!canContinue) return;

    // Resolve display start time (what the customer sees, e.g. "9:00 AM")
    let displayHH, displayMM;
    if (selectedStartTime) {
      [displayHH, displayMM] = selectedStartTime.split(':').map(Number);
    } else {
      const earliest = getEarliestStartTimeHM(selectedDateObj);
      displayHH = earliest.h;
      displayMM = earliest.m;
    }
    const displayStartStr = `${String(displayHH).padStart(2, '0')}:${String(displayMM).padStart(2, '0')}`;

    // API booking times: use noon UTC so the date is unambiguous regardless of the user's timezone.
    // Midnight UTC (00:00Z) can appear as the previous calendar day in UTC-12 to UTC-1 timezones.
    // Noon UTC (12:00Z) stays within the same calendar day in all timezones (UTC-12 to UTC+12).
    // This matches Sharetribe's own convention for day/night bookings.
    const y = selectedDateObj.getFullYear();
    const mo = selectedDateObj.getMonth();
    const d = selectedDateObj.getDate();
    const startMs = Date.UTC(y, mo, d, 12, 0, 0, 0);
    const endMs = Date.UTC(y, mo, d + selectedSlot.days, 12, 0, 0, 0);

    // Return display time: closing time on the LAST rental day (start + days - 1), not start + days.
    // e.g. 3-day rental Apr 21-23: return time is based on Apr 23, not Apr 24.
    const lastRentalDay = new Date(Date.UTC(y, mo, d + selectedSlot.days - 1, 12, 0, 0, 0));
    const returnTimeLabel = getClosingTimeLabel(lastRentalDay);

    console.log('[MultiDay handleContinue]', {
      bookingStartTime_UTC: new Date(startMs).toISOString(),
      bookingEndTime_UTC: new Date(endMs).toISOString(),
      bookingStartTime_local: new Date(startMs).toString(),
      bookingEndTime_local: new Date(endMs).toString(),
      displayStartTime: displayStartStr,
      returnTimeLabel,
      selectedSlotPrice: selectedSlot.price,
      selectedMultiDaySlot: selectedSlot,
    });

    onSubmit({
      bookingStartTime: String(startMs),
      bookingEndTime: String(endMs),
      selectedSlotPrice: selectedSlot.price,
      selectedMultiDaySlot: selectedSlot,
      pub_multiday_display_start_time: displayStartStr,
      multiDayReturnTimeLabel: returnTimeLabel,
    });
  };

  return (
    <div className={css.multiDayWidget}>
      <div className={css.multiDayField}>
        <label className={css.multiDayLabel}>Start Date</label>
        <SingleDatePicker
          id="multiDayStartDate"
          value={selectedDateObj}
          onChange={date => {
            setSelectedDateObj(date);
            // Pre-select the earliest available start time for the chosen date
            const { h, m } = getEarliestStartTimeHM(date);
            setSelectedStartTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
          }}
          isDayBlocked={isDayBlocked}
        />
      </div>

      <div className={css.multiDayField}>
        <label className={css.multiDayLabel}>Duration</label>
        <select
          className={css.multiDaySelect}
          value={selectedSlotValue}
          onChange={e => {
            setSelectedSlotValue(e.target.value);
          }}
        >
          <option value="">Select number of days…</option>
          {multiDaySlots.map(slot => {
            const key = String(slot.value || slot.days);
            const perDay = slot.pricePerDay ?? slot.price;
            const total = slot.price;
            const labelText = slot.label
              ? `${slot.days} Days · ${fmtPrice(total)} total (${fmtPrice(perDay)}/day) · ${slot.label}`
              : `${slot.days} Days · ${fmtPrice(total)} total (${fmtPrice(perDay)}/day)`;
            return (
              <option key={key} value={key}>
                {labelText}
              </option>
            );
          })}
        </select>
      </div>

      {selectedDateObj && selectedSlot ? (
        <div className={css.multiDayField}>
          <label className={css.multiDayLabel}>Start Time</label>
          <select
            className={css.multiDaySelect}
            value={selectedStartTime}
            onChange={e => setSelectedStartTime(e.target.value)}
          >
            <option value="">Select time…</option>
            {availableTimes.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {endDateObj && selectedStartTime ? (
        <p className={css.multiDayReturnInfo}>
          Returns: {fmtDate(endDateObj)} by {getClosingTimeLabel(endDateObj)}
        </p>
      ) : null}

      {quantityStepperNode || null}

      {priceBreakdown ? (
        <div className={css.multiDayPriceBreakdown}>
          <div className={css.multiDayPriceRow}>
            <span>
              {fmtPrice(priceBreakdown.perDay)}/day × {selectedSlot.days} day{selectedSlot.days !== 1 ? 's' : ''}
              {selectedSlot.label ? ` · ${selectedSlot.label}` : ''}
            </span>
            <span>{fmtPrice(priceBreakdown.basePrice)}</span>
          </div>
          <div className={css.multiDayPriceRow}>
            <span>Fees &amp; taxes</span>
            <span>{fmtPrice(priceBreakdown.fee)}</span>
          </div>
          <div className={classNames(css.multiDayPriceRow, css.multiDayPriceTotal)}>
            <span>Est. Total</span>
            <span>{fmtPrice(priceBreakdown.total)}</span>
          </div>
        </div>
      ) : null}

      {/* Push 10 Phase D.5 — age-gate message renders inline above the
          Continue button when active, matching the placement pattern of
          the other Step-1 booking forms. handleContinue itself calls
          step1OnSubmit which short-circuits when the gate fires, so the
          button is functionally a no-op even before the user reaches
          this message. */}
      {gateErrorNode ? <p className={css.ageGateError}>{gateErrorNode}</p> : null}

      <PrimaryButton type="button" disabled={!canContinue} onClick={handleContinue}>
        Continue
      </PrimaryButton>
    </div>
  );
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState(() => {
    const opts = Array.isArray(props.listing?.attributes?.publicData?.pickup_options)
      ? props.listing.attributes.publicData.pickup_options
      : [];
    return opts.length === 1 ? opts[0] : null;
  });
  const [deliveryZone, setDeliveryZone] = useState('');
  const [deliveryZoneFee, setDeliveryZoneFee] = useState(0);
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [selectedAddons, setSelectedAddons] = useState(() => {
    const addOnsFromListing = props.listing?.attributes?.publicData?.add_ons;
    const addOnsList = Array.isArray(addOnsFromListing) ? addOnsFromListing : [];
    const initial = {};
    addOnsList.forEach(addon => {
      if (addon.type === 'pool') {
        const count = addon.freeCount || 0;
        const firstOption = addon.itemOptions?.[0] || '';
        initial[addon.id] = Array(count).fill(firstOption);
      } else {
        initial[addon.id] = addon.required ? 1 : 0;
      }
    });
    return initial;
  });
  const [step, setStep] = useState(1);
  const [step1Values, setStep1Values] = useState(null);
  const [groupSize, setGroupSize] = useState(2);
  const [howMany, setHowMany] = useState(1);
  const [tripType, setTripType] = useState(() => {
    const slots = props.listing?.attributes?.publicData?.rental_slots;
    const hasHourly = Array.isArray(slots) && slots.length > 0;
    return hasHourly ? 'hourly' : 'multiday';
  });
  const [boardingAddress, setBoardingAddress] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [showSpecialRequests, setShowSpecialRequests] = useState(false);
  const [multiDayInquirySent, setMultiDayInquirySent] = useState(false);
  const [multiDayInquirySubmitting, setMultiDayInquirySubmitting] = useState(false);
  const [multiDayInquiryLocalError, setMultiDayInquiryLocalError] = useState(null);
  const [multiDayCustomNote, setMultiDayCustomNote] = useState('');
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  // Track current fee and last fetch params so we can re-fetch when zone changes
  const deliveryZoneFeeRef = useRef(deliveryZoneFee);
  deliveryZoneFeeRef.current = deliveryZoneFee;
  const selectedAddonsRef = useRef(selectedAddons);
  selectedAddonsRef.current = selectedAddons;
  const lastLineItemsParamsRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    listing,
    currentUser,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    onSendInquiry,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
  } = props;

  // Push 10 Phase D.5 — Compute age-gate state once per render. Returns
  // a FormattedMessage node when the gate fires (customer userType,
  // logged-in, but DOB missing OR age < listing minimum_renter_age) or
  // null when the gate passes. Cheap pure computation — no useMemo
  // needed; rerunning per render costs nothing.
  const step1AgeGateMessage = computeAgeGateMessage(currentUser, listing);

  const publicData = listing?.attributes?.publicData || {};
  const {
    listingType,
    unitType,
    transactionProcessAlias = '',
    priceVariants,
    startTimeInterval,
    instant_or_request_booking,
    hourly_booking_type,
    multi_day_booking_type,
    minimum_hours,
    available_slot_durations,
    rental_slots,
    multi_day_slots,
    passenger_capacity,
    pickup_options,
    approved_destinations,
    delivery_zone_1_label,
    delivery_zone_1_fee,
    delivery_zone_2_label,
    delivery_zone_2_fee,
    delivery_zone_3_label,
    delivery_zone_3_fee,
    booking_window_days,
    availability_end_date,
    add_ons,
    minimum_booking_notice_hours,
    time_between_bookings,
    availability_start_time,
    categoryLevel1,
  } = publicData || {};
  const addOns = Array.isArray(add_ons) ? add_ons : [];
  const isInstantBook = (() => {
    if (tripType === 'multiday' && multi_day_booking_type) {
      return multi_day_booking_type === 'instant' || multi_day_booking_type === 'instant-book';
    }
    if (tripType === 'hourly' && hourly_booking_type) {
      return hourly_booking_type === 'instant' || hourly_booking_type === 'instant-book';
    }
    return instant_or_request_booking === 'instant-book';
  })();

  // Compute the effective booking window in days for this specific listing:
  //   1. booking_window_days  — provider-set explicit window (e.g. 365)
  //   2. availability_end_date — provider-set hard end date; converted to days from today
  //   3. fallback              — global config value (366 days, matching the Sharetribe API limit)
  const effectiveDayCount = (() => {
    if (booking_window_days) {
      return Number(booking_window_days);
    }
    if (availability_end_date) {
      const endDate = new Date(availability_end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const msPerDay = 24 * 60 * 60 * 1000;
      const daysUntilEnd = Math.ceil((endDate - today) / msPerDay);
      return Math.max(daysUntilEnd, 1);
    }
    return dayCountAvailableForBooking;
  })();

  const pickupOptions = Array.isArray(pickup_options) ? pickup_options : [];
  const showDeliverySection = pickupOptions.some(o =>
    DELIVERY_OPTIONS_REQUIRING_SECTION.includes(o)
  );
  const deliveryZones = [
    { label: delivery_zone_1_label, fee: delivery_zone_1_fee },
    { label: delivery_zone_2_label, fee: delivery_zone_2_fee },
    { label: delivery_zone_3_label, fee: delivery_zone_3_fee },
  ].filter(z => z.label && z.fee != null);

  const listingAddress = publicData?.location?.address || '';
  const listingAddressShort = truncateAddressToCity(listingAddress);

  useEffect(() => {
    if (deliveryOption === 'customer-tow-away') {
      setDeliveryLocation(listingAddressShort);
    } else {
      setDeliveryLocation('');
    }
    setDeliveryZone('');
    setDeliveryZoneFee(0);
  }, [deliveryOption]);

  // Pre-select delivery option on mount based on listing type
  useEffect(() => {
    if (!showDeliverySection) return;
    // If only one fulfillment option is available, auto-select it
    if (pickupOptions.length === 1) {
      setDeliveryOption(pickupOptions[0]);
      return;
    }
    if (['boat-rental', 'captained-charter-private'].includes(listingType) && pickupOptions.includes('board-at-home-base')) {
      setDeliveryOption('board-at-home-base');
    } else if (listingType === 'accessory-rental' && pickupOptions.includes('customer-pickup')) {
      setDeliveryOption('customer-pickup');
    }
  }, []);

  // Fallback: if pickupOptions resolves to a single item and deliveryOption is still null, select it
  useEffect(() => {
    if (pickupOptions.length === 1 && deliveryOption == null) {
      setDeliveryOption(pickupOptions[0]);
    }
  }, [pickupOptions]);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation;

  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const isDockRampDelivery = categoryLevel1 === 'dock-ramp-delivery';
  const serviceMenu = Array.isArray(publicData?.service_menu) ? publicData.service_menu : [];
  const showDockRampDeliveryWidget = mounted && isDockRampDelivery && !isClosed;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  // Accessory-rental listings use LINE_ITEM_DAY but we treat them with the time-based widget
  // when they have rental_slots configured (slot price overrides quantity calculation on server).
  const isAccessoryRentalWithSlots =
    listingType === 'accessory-rental' &&
    Array.isArray(rental_slots) && rental_slots.length > 0;
  const showBookingTimeForm =
    mounted &&
    (shouldHaveBookingTime || isAccessoryRentalWithSlots) &&
    !isClosed &&
    timeZone &&
    !isDockRampDelivery;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  // Wrap onFetchTransactionLineItems to inject deliveryZoneFee and selectedAddons into every call,
  // and re-fetch when the selected zone (and fee) or add-ons change.
  // The returned promise is suppressed on error — a line-items fetch failure only affects
  // the estimated total display and must never block checkout navigation.
  const onFetchTransactionLineItemsWithDelivery = params => {
    const fee = deliveryZoneFeeRef.current;
    const activeAddons = buildActiveAddons(addOns, selectedAddonsRef.current);
    lastLineItemsParamsRef.current = params;
    const enhancedOrderData = {
      ...params.orderData,
      ...(fee > 0 ? { deliveryZoneFee: fee } : {}),
      ...(activeAddons.length > 0 ? { selectedAddons: activeAddons } : {}),
    };
    const p = onFetchTransactionLineItems({ ...params, orderData: enhancedOrderData });
    // Suppress the unhandled rejection that comes from .unwrap() inside fetchTransactionLineItems
    if (p && typeof p.catch === 'function') p.catch(() => {});
    return p;
  };

  const refetchLineItemsIfNeeded = (overrides = {}) => {
    const last = lastLineItemsParamsRef.current;
    if (!last || fetchLineItemsInProgress) return;
    const { bookingStart, bookingEnd } = last.orderData || {};
    if (!bookingStart || !bookingEnd) return;
    const fee = deliveryZoneFeeRef.current;
    const activeAddons = buildActiveAddons(addOns, selectedAddonsRef.current);
    const enhancedOrderData = {
      ...last.orderData,
      ...(fee > 0 ? { deliveryZoneFee: fee } : {}),
      ...(activeAddons.length > 0 ? { selectedAddons: activeAddons } : {}),
      ...overrides,
    };
    onFetchTransactionLineItems({ ...last, orderData: enhancedOrderData });
  };

  useEffect(() => {
    refetchLineItemsIfNeeded({ deliveryZoneFee: deliveryZoneFee > 0 ? deliveryZoneFee : undefined });
  }, [deliveryZoneFee]); // eslint-disable-line

  useEffect(() => {
    refetchLineItemsIfNeeded();
  }, [selectedAddons]); // eslint-disable-line

  // Push 10 Phase D.5 — gated wrapper around props.onSubmit. Single
  // chokepoint for ALL transaction-initiating paths reachable from
  // OrderPanel (currently active: booking widgets via Step 2; latent
  // but defensively covered: ProductOrderForm, InquiryWithoutPaymentForm,
  // NegotiationForm, NegotiationRequestQuoteForm). When step1AgeGateMessage
  // is non-null, the submit is silently blocked — the message is rendered
  // separately inline. Defense-in-depth alongside step1OnSubmit (which
  // also short-circuits, so the booking flow has two layers) and the
  // server-side gate at server/api/initiate-privileged.js.
  const gatedOnSubmit = values => {
    if (step1AgeGateMessage) return undefined;
    return onSubmit(values);
  };

  // Wrap onSubmit to inject delivery data and selected add-ons into the order data
  const onSubmitWithDelivery = values => {
    const deliveryData = showDeliverySection
      ? { deliveryOption, deliveryZone, deliveryZoneFee, deliveryLocation }
      : {};
    const activeAddons = buildActiveAddons(addOns, selectedAddons);
    const addonsData = activeAddons.length > 0 ? { selectedAddons: activeAddons } : {};
    const extraData = {};
    if (boardingAddress) extraData.boardingAddress = boardingAddress;
    if (specialRequests) extraData.specialRequests = specialRequests;
    return gatedOnSubmit({ ...values, ...deliveryData, ...addonsData, ...(isDockRampDelivery ? {} : { groupSize }), ...extraData });
  };

  // Step 1 submit: save form values and advance to step 2 (review)
  // Push 10 Phase D.5 — short-circuit when the age gate is active.
  // The gate message is already rendered inline above the submit
  // button via step1FooterNode below, so no toast / re-render needed;
  // we just block the step-2 advance. Server-side gate at
  // initiate-privileged.js stays as defense-in-depth.
  const step1OnSubmit = values => {
    if (step1AgeGateMessage) return;
    setStep1Values(values);
    setStep(2);
  };

  const handleMultiDayInquiry = () => {
    const multiDaySlot = step1Values?.selectedMultiDaySlot;
    if (!multiDaySlot || !step1Values?.bookingStartTime) return;

    const startMs = parseInt(step1Values.bookingStartTime, 10);
    const startDate = new Date(startMs);
    const endDate = new Date(startMs + (multiDaySlot.days - 1) * 86400000);

    const fmtDate = (d, includeYear) => {
      const opts = { weekday: 'short', month: 'short', day: 'numeric' };
      if (includeYear) opts.year = 'numeric';
      return d.toLocaleDateString('en-US', opts);
    };

    const pricePerDay = multiDaySlot.pricePerDay != null
      ? Math.round(multiDaySlot.pricePerDay / 100).toLocaleString('en-US')
      : null;
    const totalPrice = Math.round(multiDaySlot.price / 100).toLocaleString('en-US');
    const days = multiDaySlot.days;
    const passengers = groupSize;

    const pricePerDayLine = pricePerDay
      ? `$${pricePerDay}/day · $${totalPrice} total`
      : `$${totalPrice} total`;

    const customNote = multiDayCustomNote.trim();
    const message = [
      "Hi! I'd like to request a multi-day rental.",
      '',
      `Dates: ${fmtDate(startDate, false)} – ${fmtDate(endDate, true)}`,
      `Duration: ${days} Day${days !== 1 ? 's' : ''}`,
      `Quoted rate: ${pricePerDayLine}`,
      `Group size: ${passengers} passenger${passengers !== 1 ? 's' : ''}`,
      '',
      ...(customNote ? [`${customNote}`, ''] : []),
      'Please confirm availability and I\'ll complete the booking.',
    ].join('\n');

    setMultiDayInquirySubmitting(true);
    setMultiDayInquiryLocalError(null);

    onSendInquiry(listing, message)
      .then(() => {
        setMultiDayInquirySent(true);
        setMultiDayInquirySubmitting(false);
      })
      .catch(err => {
        setMultiDayInquiryLocalError('Something went wrong. Please try again.');
        setMultiDayInquirySubmitting(false);
      });
  };

  const renderStep2 = () => {
    const startTs = step1Values?.bookingStartTime;
    const endTs = step1Values?.bookingEndTime;
    const startDateObj = startTs
      ? new Date(parseInt(startTs, 10))
      : step1Values?.bookingDates?.startDate || null;
    const endDateObj = endTs
      ? new Date(parseInt(endTs, 10))
      : step1Values?.bookingDates?.endDate || null;

    const fmtOpts = { timeZone: timeZone || undefined };
    const dateStr = startDateObj
      ? intl.formatDate(startDateObj, { ...fmtOpts, weekday: 'short', month: 'short', day: 'numeric' })
      : null;
    const startTimeStr =
      startTs && startDateObj
        ? intl.formatTime(startDateObj, { ...fmtOpts, hour: 'numeric', minute: '2-digit' })
        : null;
    const endTimeStr =
      endTs && endDateObj
        ? intl.formatTime(endDateObj, { ...fmtOpts, hour: 'numeric', minute: '2-digit' })
        : null;

    const durationMs = startTs && endTs ? parseInt(endTs, 10) - parseInt(startTs, 10) : 0;
    const durationHours = durationMs > 0 ? Math.round(durationMs / 3600000) : 0;

    const providerFirstName = props.author?.attributes?.profile?.firstName || 'The provider';

    const isMultiDaySummary = tripType === 'multiday' && !!step1Values?.selectedMultiDaySlot;
    const multiDaySlot = step1Values?.selectedMultiDaySlot;

    let durationStr;
    let summaryLine1;
    let summaryLine2;

    if (isMultiDaySummary) {
      const days = multiDaySlot.days;
      durationStr = `${days} Day${days !== 1 ? 's' : ''}`;
      summaryLine1 = [dateStr, durationStr].filter(Boolean).join(' · ') || 'Your selection';

      // Format the stored display start time (e.g. "09:00" → "9:00 AM")
      const displayStartRaw = step1Values.pub_multiday_display_start_time;
      let displayStartLabel = null;
      if (displayStartRaw && startDateObj) {
        const [dh, dm] = displayStartRaw.split(':').map(Number);
        const displayDate = new Date(startDateObj);
        displayDate.setHours(dh, dm, 0, 0);
        displayStartLabel = intl.formatTime(displayDate, { ...fmtOpts, hour: 'numeric', minute: '2-digit' });
      }

      // Display end date = last rental day (start + days - 1), not the API end (start + days).
      // e.g. 3-day Apr 21 rental: display Apr 23, not Apr 24.
      const displayEndMs = startTs
        ? parseInt(startTs, 10) + (multiDaySlot.days - 1) * 86400000
        : null;
      const displayEndDateObj = displayEndMs ? new Date(displayEndMs) : null;
      const returnDateStr = displayEndDateObj
        ? intl.formatDate(displayEndDateObj, { ...fmtOpts, weekday: 'short', month: 'short', day: 'numeric' })
        : null;
      const returnTimeLabel = step1Values.multiDayReturnTimeLabel || '5:00 PM';
      summaryLine2 =
        displayStartLabel && returnDateStr
          ? `${displayStartLabel} → ${returnDateStr} by ${returnTimeLabel}`
          : displayStartLabel || null;
    } else {
      durationStr = durationHours > 0 ? `${durationHours} Hour${durationHours !== 1 ? 's' : ''}` : null;
      summaryLine1 = [dateStr, durationStr].filter(Boolean).join(' · ') || 'Your selection';
      summaryLine2 = startTimeStr && endTimeStr ? `${startTimeStr} – ${endTimeStr}` : null;
    }

    const customerItems = (lineItems || []).filter(
      item => item.includeFor?.includes('customer') && item.quantity !== 0
    );
    const currency = price?.currency || marketplaceCurrency;

    const sumItems = arr =>
      arr.reduce((s, item) => {
        const lt =
          item.lineTotal?.amount ??
          item.unitPrice?.amount *
            (item.quantity ?? (item.units != null ? item.units * item.seats : 1));
        return s + (lt || 0);
      }, 0);

    const total = (() => {
      if (isMultiDaySummary && multiDaySlot?.price) {
        const calculatedFee = Math.round(multiDaySlot.price * (FEE_PERCENT / 100));
        const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
        return multiDaySlot.price + actualFee;
      }
      return sumItems(customerItems);
    })();

    const estTotalStr =
      total > 0
        ? (() => {
            const isWholeDollar = total % 100 === 0;
            return `Est. ${intl.formatNumber(total / 100, {
              style: 'currency',
              currency,
              maximumFractionDigits: isWholeDollar ? 0 : 2,
            })}`;
          })()
        : null;

    const rentalCodes = ['line-item/hour', 'line-item/day', 'line-item/night', 'line-item/fixed'];
    const rentalItems = customerItems.filter(item => rentalCodes.includes(item.code));
    const addonItems = customerItems.filter(item => item.code?.startsWith('line-item/addon-'));
    const deliveryItems = customerItems.filter(item => item.code === 'line-item/delivery-fee');
    const otherItems = customerItems.filter(
      item =>
        !rentalCodes.includes(item.code) &&
        !item.code?.startsWith('line-item/addon-') &&
        item.code !== 'line-item/delivery-fee'
    );

    const rentalTotal = sumItems(rentalItems);
    const addonTotal = sumItems(addonItems);
    const deliveryTotal = sumItems(deliveryItems);
    const otherTotal = sumItems(otherItems);

    const feeItems = otherItems.filter(item => item.code === 'line-item/customer-commission');
    const taxItems = otherItems.filter(item => item.code === 'line-item/sales-tax');
    const remainingFeeItems = otherItems.filter(
      item => item.code !== 'line-item/customer-commission' && item.code !== 'line-item/sales-tax'
    );
    const feeTotal = sumItems(feeItems);
    const taxTotal = sumItems(taxItems);
    const remainingFeeTotal = sumItems(remainingFeeItems);

    const formatAmt = amt => {
      const isWhole = amt % 100 === 0;
      return intl.formatNumber(amt / 100, {
        style: 'currency',
        currency,
        maximumFractionDigits: isWhole ? 0 : 2,
      });
    };

    const rentalLabel = isMultiDaySummary
      ? `Boat (${multiDaySlot.days} day${multiDaySlot.days !== 1 ? 's' : ''})`
      : durationHours > 0
      ? `Boat (${durationHours} hr${durationHours !== 1 ? 's' : ''})`
      : 'Rental';

    const effectivePickupOptions =
      pickupOptions.length > 0 ? pickupOptions : ['board-at-home-base'];

    return (
      <div className={css.step2Container}>
        {/* Summary bar */}
        <div className={css.selectionSummaryBar}>
          <div className={css.selectionSummaryTop}>
            <span className={css.selectionSummaryDate}>
                {summaryLine1}
                {summaryLine2 ? <span style={{ display: 'block', fontWeight: 400 }}>{summaryLine2}</span> : null}
              </span>
            <button type="button" className={css.step2EditLink} onClick={() => setStep(1)}>
              Edit
            </button>
          </div>
          {null}
        </div>

        {/* Delivery section — always shown */}
        <DeliverySection
          pickupOptions={effectivePickupOptions}
          approvedDestinations={approved_destinations}
          deliveryZones={deliveryZones}
          deliveryOption={deliveryOption}
          setDeliveryOption={setDeliveryOption}
          deliveryZone={deliveryZone}
          setDeliveryZone={setDeliveryZone}
          setDeliveryZoneFee={setDeliveryZoneFee}
          deliveryLocation={deliveryLocation}
          setDeliveryLocation={setDeliveryLocation}
          intl={intl}
        />

        {/* Requested delivery location (provider/3rd-party only) + add extras + special requests */}
        <div className={css.extraFieldsSection}>
          {(deliveryOption === 'provider-delivers' || deliveryOption === 'third-party-ok') ? (
          <div className={css.extraField}>
            <label className={css.extraFieldLabel}>{deliveryOption === 'provider-delivers' ? 'REQUESTED DELIVERY LOCATION' : 'REQUESTED DELIVERY ADDRESS'}</label>
            <input
              type="text"
              className={css.extraFieldInput}
              placeholder="e.g. Slip 12, Ski Beach Marina, Tahoma, CA"
              value={boardingAddress}
              onChange={e => setBoardingAddress(e.target.value)}
            />
          </div>
          ) : null}
          {addOns.length > 0 ? (
            <div style={{ marginTop: '12px' }}>
              <AddOnsSelector
                addOns={addOns}
                selectedAddons={selectedAddons}
                setSelectedAddons={setSelectedAddons}
              />
            </div>
          ) : null}
          <div className={css.extraField} style={{ marginTop: '12px' }}>
            <button
              type="button"
              onClick={() => setShowSpecialRequests(prev => !prev)}
              style={{
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                padding: '10px 16px',
                width: '100%',
                textAlign: 'left',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#444',
              }}
            >
              {showSpecialRequests ? '− Special requests' : '+ Special requests'}
            </button>
            {showSpecialRequests ? (
              <textarea
                className={css.extraFieldInput}
                rows={3}
                style={{ marginTop: '16px' }}
                placeholder="Any special requests, accessibility needs, or notes for your provider"
                value={specialRequests}
                onChange={e => setSpecialRequests(e.target.value)}
              />
            ) : null}
          </div>
        </div>

        {/* Sticky price summary + CTA */}
        <div className={css.stickyPriceSummary}>
          {isMultiDaySummary ? (
            (() => {
              const slotBase = multiDaySlot.price;
              const calculatedFee = Math.round(slotBase * (FEE_PERCENT / 100));
              const slotFee = Math.max(calculatedFee, MINIMUM_FEE);
              const slotTotal = slotBase + slotFee + sumItems(addonItems) + sumItems(deliveryItems);
              return (
                <>
                  <div className={css.summaryLineItem}>
                    <span>{rentalLabel}</span>
                    <span>{formatAmt(slotBase)}</span>
                  </div>
                  {sumItems(addonItems) > 0 ? (
                    <div className={css.summaryLineItem}>
                      <span>Extras</span>
                      <span>{formatAmt(sumItems(addonItems))}</span>
                    </div>
                  ) : null}
                  {sumItems(deliveryItems) > 0 ? (
                    <div className={css.summaryLineItem}>
                      <span>Delivery</span>
                      <span>{formatAmt(sumItems(deliveryItems))}</span>
                    </div>
                  ) : null}
                  <div className={css.summaryLineItem}>
                    <span>Fees &amp; taxes</span>
                    <span>{formatAmt(slotFee)}</span>
                  </div>
                  <hr className={css.summaryDivider} />
                  <div className={css.summaryTotal}>
                    <span>Total</span>
                    <span>{formatAmt(slotTotal)}</span>
                  </div>
                  <p className={css.summaryTotalNote}>Includes fees &amp; taxes</p>
                </>
              );
            })()
          ) : customerItems.length > 0 && !fetchLineItemsInProgress ? (
            <>
              {rentalTotal > 0 ? (
                <div className={css.summaryLineItem}>
                  <span>{rentalLabel}</span>
                  <span>{formatAmt(rentalTotal)}</span>
                </div>
              ) : null}
              {addonTotal > 0 ? (
                <div className={css.summaryLineItem}>
                  <span>Extras</span>
                  <span>{formatAmt(addonTotal)}</span>
                </div>
              ) : null}
              {deliveryTotal > 0 ? (
                <div className={css.summaryLineItem}>
                  <span>Delivery</span>
                  <span>{formatAmt(deliveryTotal)}</span>
                </div>
              ) : null}
              {feeTotal > 0 ? (
                <div className={css.summaryLineItem}>
                  <span>Booking Fee</span>
                  <span>{formatAmt(feeTotal)}</span>
                </div>
              ) : null}
              {taxTotal > 0 ? (
                <div className={css.summaryLineItem}>
                  <span>Tax</span>
                  <span>{formatAmt(taxTotal)}</span>
                </div>
              ) : null}
              {remainingFeeTotal !== 0 ? (
                <div className={css.summaryLineItem}>
                  <span>Fees &amp; taxes</span>
                  <span>{formatAmt(remainingFeeTotal)}</span>
                </div>
              ) : null}
              <hr className={css.summaryDivider} />
              <div className={css.summaryTotal}>
                <span>Total</span>
                <span>{formatAmt(total)}</span>
              </div>
              <p className={css.summaryTotalNote}>Includes fees &amp; taxes</p>
            </>
          ) : fetchLineItemsInProgress ? (
            <p className={css.summaryLoading}>Calculating…</p>
          ) : null}

          {isMultiDaySummary ? (
            multiDayInquirySent ? (
              <div className={css.inquirySuccess}>
                <div className={css.inquirySuccessIcon}>✓</div>
                <h3>Booking request sent!</h3>
                <p>
                  {providerFirstName} will review your request and respond within 2 hours.
                  You'll receive a notification when they reply.
                </p>
                <a href="/inbox/orders">View your message →</a>
              </div>
            ) : (
              <>
                {multiDayInquiryLocalError ? (
                  <p className={css.inquiryError}>{multiDayInquiryLocalError}</p>
                ) : null}
                <div className={css.multiDayNoteField}>
                  <label className={css.multiDayNoteLabel}>Message to provider (optional)</label>
                  <textarea
                    className={css.multiDayNoteTextarea}
                    rows={3}
                    placeholder="Add any questions or details for the provider…"
                    value={multiDayCustomNote}
                    onChange={e => setMultiDayCustomNote(e.target.value)}
                  />
                </div>
                <p className={css.multiDayCtaNote}>
                  Your request will be sent to the provider. Most respond within 2 hours.
                </p>
                <PrimaryButton
                  type="button"
                  onClick={handleMultiDayInquiry}
                  inProgress={multiDayInquirySubmitting}
                  disabled={multiDayInquirySubmitting}
                >
                  {multiDayInquirySubmitting ? 'Sending…' : 'Send Booking Request'}
                </PrimaryButton>
                <button type="button" className={css.step2BackLink} onClick={() => setStep(1)}>
                  ← Back to edit dates
                </button>
              </>
            )
          ) : (
            <>
              <PrimaryButton
                type="button"
                onClick={() => onSubmitWithDelivery(step1Values)}
                inProgress={fetchLineItemsInProgress}
              >
                {isInstantBook ? 'Book Now' : 'Request to Book'}
              </PrimaryButton>
              <p className={css.deliveryDisclaimer}>
                Your provider will confirm the exact meeting point after booking.
              </p>
              <button type="button" className={css.step2BackLink} onClick={() => setStep(1)}>
                ← Back to edit dates
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Step 1 breakdown should not show add-on line items — the customer hasn't selected them yet.
  const lineItemsForStep1 = Array.isArray(lineItems)
    ? lineItems.filter(item => !item.code?.startsWith('line-item/addon-'))
    : lineItems;

  // Estimated total shown in Step 1 instead of the detailed breakdown.
  // Shows actual total after line items fetch, or a placeholder from the pre-selected slot.
  const maxPassengers = passenger_capacity ? Number(passenger_capacity) : 99;
  // Hide group size for accessory-rental (they use "How many?" stepper instead)
  const groupSizeStepperNode =
    listingType === 'captained-charter-ticketed' || listingType === 'accessory-rental' ? null : (
      <div className={css.groupSizeSection}>
        <span className={css.groupSizeLabel}>Group size</span>
        <div className={css.groupSizeStepper}>
          <button
            type="button"
            className={css.stepperBtn}
            onClick={() => setGroupSize(s => Math.max(1, s - 1))}
            disabled={groupSize <= 1}
          >
            −
          </button>
          <span className={css.stepperValue}>{groupSize}</span>
          <button
            type="button"
            className={css.stepperBtn}
            onClick={() => setGroupSize(s => Math.min(maxPassengers, s + 1))}
            disabled={groupSize >= maxPassengers}
          >
            +
          </button>
        </div>
        <span className={css.groupSizeMax}>
          {passenger_capacity ? `Max ${passenger_capacity} passengers` : ''}
        </span>
      </div>
    );

  // Compact group size widget for mobile booking form grid (no border-top/padding)
  const isRentalSlots = Array.isArray(rental_slots) && rental_slots.length > 0;
  const groupSizeMobileNode =
    !isRentalSlots || listingType === 'captained-charter-ticketed' || listingType === 'accessory-rental'
      ? null
      : (
        <div className={css.groupSizeMobileCompact}>
          <span className={css.groupSizeLabel}>
            Group size
          </span>
          <div className={css.groupSizeStepper}>
            <button
              type="button"
              className={css.stepperBtn}
              onClick={() => setGroupSize(s => Math.max(1, s - 1))}
              disabled={groupSize <= 1}
            >
              −
            </button>
            <span className={css.stepperValue}>{groupSize}</span>
            <button
              type="button"
              className={css.stepperBtn}
              onClick={() => setGroupSize(s => Math.min(maxPassengers, s + 1))}
              disabled={groupSize >= maxPassengers}
            >
              +
            </button>
          </div>
        </div>
      );

  // "How many?" stepper for accessory-rental — caps at listing's seat count or 10
  const maxHowMany = listingType === 'accessory-rental'
    ? (publicData?.seats ? Number(publicData.seats) : 10)
    : 1;
  const showHowMany = listingType === 'accessory-rental' && maxHowMany > 1;
  const howManyStepperNode = showHowMany ? (
    <div className={css.groupSizeSection}>
      <span className={css.groupSizeLabel}>How many?</span>
      <div className={css.groupSizeStepper}>
        <button
          type="button"
          className={css.stepperBtn}
          onClick={() => setHowMany(s => Math.max(1, s - 1))}
          disabled={howMany <= 1}
        >
          −
        </button>
        <span className={css.stepperValue}>{howMany}</span>
        <button
          type="button"
          className={css.stepperBtn}
          onClick={() => setHowMany(s => Math.min(maxHowMany, s + 1))}
          disabled={howMany >= maxHowMany}
        >
          +
        </button>
      </div>
      <span className={css.groupSizeMax}>Max {maxHowMany}</span>
    </div>
  ) : null;

  const step1EstimatedTotalNode = (() => {
    const currency = price?.currency || marketplaceCurrency;

    // Actual total once line items are available.
    const customerItems = Array.isArray(lineItemsForStep1)
      ? lineItemsForStep1.filter(
          item => item.includeFor?.includes('customer') && item.quantity !== 0
        )
      : [];
    if (customerItems.length > 0 && !fetchLineItemsInProgress) {
      const total = customerItems.reduce((sum, item) => {
        const lt =
          item.lineTotal?.amount ??
          item.unitPrice?.amount *
            (item.quantity ?? (item.units != null ? item.units * item.seats : 1));
        return sum + (lt || 0);
      }, 0);
      if (total > 0) {
        const isWholeDollar = total % 100 === 0;
        const formattedTotal = intl.formatNumber(total / 100, {
          style: 'currency',
          currency,
          maximumFractionDigits: isWholeDollar ? 0 : 2,
        });
        return (
          <>
            {isRentalSlots ? (
              <div className={css.groupSizeDesktopOnly}>{groupSizeStepperNode}</div>
            ) : groupSizeStepperNode}
            <div className={css.estimatedTotal}>
              <span className={css.estimatedTotalAmount}>Est. {formattedTotal}</span>
              <p className={css.estimatedTotalNote}>Includes fees. Extras may change final price.</p>
            </div>
          </>
        );
      }
    }

    // Placeholder total from the pre-selected (middle) rental slot before a date is chosen.
    if (Array.isArray(rental_slots) && rental_slots.length > 0 && currency) {
      const defaultSlot = rental_slots.slice().sort((a, b) => a.hours - b.hours)[0];
      if (defaultSlot?.price) {
        const calculatedFee = Math.round(defaultSlot.price * (FEE_PERCENT / 100));
        const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
        const rawTotal = defaultSlot.price + actualFee;
        const roundedTotal = Math.ceil(rawTotal / 100) * 100;
        const isWholeDollar = roundedTotal % 100 === 0;
        const formattedTotal = intl.formatNumber(roundedTotal / 100, {
          style: 'currency',
          currency,
          maximumFractionDigits: isWholeDollar ? 0 : 2,
        });
        return (
          <>
            <div className={css.groupSizeDesktopOnly}>{groupSizeStepperNode}</div>
            <div className={css.estimatedTotal}>
              <span className={css.estimatedTotalAmount}>Est. {formattedTotal}</span>
              <p className={css.estimatedTotalNote}>Based on your selection</p>
            </div>
          </>
        );
      }
    }

    return listingType === 'accessory-rental' ? howManyStepperNode : groupSizeStepperNode;
  })();

  // Push 10 Phase D.5 — wrap step1EstimatedTotalNode with an inline
  // age-gate error rendered BELOW the existing footer content. The
  // booking forms render `estimatedTotalNode` directly above their
  // submit button (e.g. BookingDatesForm.js:851 → submit at :854), so
  // appending the gate error here places it "above the submit button,
  // below the user's last input" per the Phase D.5 placement decision.
  // Zero form-component edits required.
  const step1FooterNode =
    step1AgeGateMessage != null ? (
      <>
        {step1EstimatedTotalNode}
        <p className={css.ageGateError}>{step1AgeGateMessage}</p>
      </>
    ) : (
      step1EstimatedTotalNode
    );

  const sharedProps = {
    lineItemUnitType,
    onSubmit: onSubmitWithDelivery,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems: onFetchTransactionLineItemsWithDelivery,
    deliverySectionNode: null, // shown separately in step 2
    lineItems: lineItemsForStep1,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    isInstantBook,
    availableSlotDurations: available_slot_durations || null,
    minimumHours: minimum_hours || null,
    rentalSlots: rental_slots || null,
    multiDaySlots: Array.isArray(multi_day_slots) && multi_day_slots.length > 0 ? multi_day_slots : null,
    minimumBookingNoticeHours: minimum_booking_notice_hours || null,
    timeBetweenBookings: time_between_bookings || null,
    availabilityStartTime: availability_start_time || null,
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        scrollLayerClassName={css.modalScrollLayer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          categoryLevel1={categoryLevel1}
          serviceMenu={serviceMenu}
        />

        {(showBookingTimeForm || showBookingFixedDurationForm || showBookingDatesForm) ? (
          <p className={css.stepIndicator}>
            {step === 1 ? (
              <>STEP 1 OF 2<span className={css.stepIndicatorSuffix}> — CHOOSE YOUR DATE & DURATION</span></>
            ) : (
              <>STEP 2 OF 2<span className={css.stepIndicatorSuffix}> — CUSTOMIZE YOUR EXPERIENCE</span></>
            )}
          </p>
        ) : null}

        {(() => {
          // Dock-ramp-delivery shows service area info elsewhere on the listing
          // page; the widget skips it for vertical compactness so the Request
          // Service CTA stays above the fold.
          if (isDockRampDelivery) return null;
          const isServiceAreaCategory = categoryLevel1 === 'captains';
          const locationValue = isServiceAreaCategory
            ? publicData?.service_area || listingAddressShort
            : listingAddressShort;
          if (!locationValue) return null;
          return (
            <div className={css.listingLocation}>
              <span className={css.listingLocationLabel}>
                {isServiceAreaCategory ? 'Service Area' : 'Vessel Home Base'}
              </span>
              <p className={classNames(css.listingLocationValue, isServiceAreaCategory && css.listingLocationValueDock)}>
                {locationValue}
              </p>
            </div>
          );
        })()}

        {(showBookingTimeForm || showBookingFixedDurationForm || showBookingDatesForm) ? (
          <hr className={css.zoneDivider} />
        ) : null}

        {(() => {
          const hasHourlySlots = Array.isArray(rental_slots) && rental_slots.length > 0;
          const hasMultiDaySlots = Array.isArray(multi_day_slots) && multi_day_slots.length > 0;
          const isHourlyListingType = listingType === 'boat-rental' || listingType === 'accessory-rental';
          const showTripTypeSelector =
            (showBookingTimeForm || showBookingDatesForm) &&
            hasHourlySlots &&
            hasMultiDaySlots &&
            !isHourlyListingType &&
            step === 1;
          if (!showTripTypeSelector) return null;
          return (
            <TripTypeSelector
              tripType={tripType}
              setTripType={setTripType}
              onReset={() => setStep1Values(null)}
            />
          );
        })()}

        {showDockRampDeliveryWidget ? (
          <DockRampDeliveryWidget
            serviceMenu={serviceMenu}
            onFetchTransactionLineItems={onFetchTransactionLineItemsWithDelivery}
            onSubmit={onSubmitWithDelivery}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            isInstantBook={isInstantBook}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            price={price}
            addOns={addOns}
            selectedAddons={selectedAddons}
            setSelectedAddons={setSelectedAddons}
          />
        ) : showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : (showBookingTimeForm || showBookingDatesForm) &&
          tripType === 'multiday' &&
          !['boat-rental', 'accessory-rental'].includes(listingType) &&
          Array.isArray(multi_day_slots) &&
          multi_day_slots.length > 0 ? (
          <>
            {step === 1 ? (
              <p className={css.multiDayNote}>
                Multi-day rentals are confirmed by the provider. Select your dates, review pricing, and send a request.
              </p>
            ) : null}
            {step === 2 ? renderStep2() : null}
            <div className={css.bookingForm} style={step === 2 ? { display: 'none' } : undefined}>
              <MultiDayBookingWidget
                multiDaySlots={multi_day_slots}
                availabilityPlan={listing?.attributes?.availabilityPlan}
                timeZone={timeZone}
                onSubmit={step1OnSubmit}
                isInstantBook={isInstantBook}
                price={price}
                marketplaceCurrency={marketplaceCurrency}
                intl={intl}
                quantityStepperNode={
                  listingType === 'accessory-rental' ? howManyStepperNode : groupSizeStepperNode
                }
                gateErrorNode={step1AgeGateMessage}
              />
            </div>
          </>
        ) : showBookingFixedDurationForm ? (
          <>
            {step === 2 ? renderStep2() : null}
            <div style={step === 2 ? { display: 'none' } : undefined}>
              <BookingFixedDurationForm
                seatsEnabled={seatsEnabled}
                className={css.bookingForm}
                formId="OrderPanelBookingFixedDurationForm"
                dayCountAvailableForBooking={effectiveDayCount}
                monthlyTimeSlots={monthlyTimeSlots}
                timeSlotsForDate={timeSlotsForDate}
                onFetchTimeSlots={onFetchTimeSlots}
                startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
                startTimeInterval={startTimeInterval}
                timeZone={timeZone}
                finePrintComponent={SubmitFinePrint}
                submitButtonText={isInstantBook ? 'Instant Book' : 'Request to Book'}
                estimatedTotalNode={step1FooterNode}
                {...priceVariantsMaybe}
                {...sharedProps}
                onSubmit={step1OnSubmit}
              />
            </div>
          </>
        ) : showBookingTimeForm ? (
          <>
            {step === 2 ? renderStep2() : null}
            <div style={step === 2 ? { display: 'none' } : undefined}>
              <BookingTimeForm
                seatsEnabled={listingType === 'accessory-rental' ? false : seatsEnabled}
                className={css.bookingForm}
                formId="OrderPanelBookingTimeForm"
                dayCountAvailableForBooking={effectiveDayCount}
                monthlyTimeSlots={monthlyTimeSlots}
                timeSlotsForDate={timeSlotsForDate}
                onFetchTimeSlots={onFetchTimeSlots}
                startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
                endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
                timeZone={timeZone}
                finePrintComponent={SubmitFinePrint}
                submitButtonText={isInstantBook ? 'Instant Book' : 'Request to Book'}
                estimatedTotalNode={step1FooterNode}
                groupSizeMobileNode={groupSizeMobileNode}
                {...priceVariantsMaybe}
                {...sharedProps}
                onSubmit={step1OnSubmit}
              />
            </div>
          </>
        ) : showBookingDatesForm ? (
          <>
            {step === 2 ? renderStep2() : null}
            <div style={step === 2 ? { display: 'none' } : undefined}>
              <BookingDatesForm
                seatsEnabled={seatsEnabled}
                className={css.bookingForm}
                formId="OrderPanelBookingDatesForm"
                dayCountAvailableForBooking={effectiveDayCount}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                timeZone={timeZone}
                finePrintComponent={SubmitFinePrint}
                submitButtonText={isInstantBook ? 'Instant Book' : 'Request to Book'}
                estimatedTotalNode={step1FooterNode}
                {...priceVariantsMaybe}
                {...sharedProps}
                onSubmit={step1OnSubmit}
              />
            </div>
          </>
        ) : showProductOrderForm ? (
          <>
            {step1AgeGateMessage ? (
              <p className={css.ageGateError}>{step1AgeGateMessage}</p>
            ) : null}
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              currentStock={currentStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              onContactUser={onContactUser}
              {...sharedProps}
            />
          </>
        ) : showInquiryForm ? (
          <>
            {step1AgeGateMessage ? (
              <p className={css.ageGateError}>{step1AgeGateMessage}</p>
            ) : null}
            <InquiryWithoutPaymentForm
              formId="OrderPanelInquiryForm"
              onSubmit={gatedOnSubmit}
              finePrintComponent={SubmitFinePrint}
              isOwnListing={isOwnListing}
            />
          </>
        ) : showNegotiationForm ? (
          <>
            {step1AgeGateMessage ? (
              <p className={css.ageGateError}>{step1AgeGateMessage}</p>
            ) : null}
            <NegotiationForm
              formId="OrderPanelNegotiationForm"
              onSubmit={gatedOnSubmit}
              finePrintComponent={SubmitFinePrint}
              payoutDetailsWarning={payoutDetailsWarning}
              isOwnListing={isOwnListing}
            />
          </>
        ) : showRequestQuoteForm ? (
          <>
            {step1AgeGateMessage ? (
              <p className={css.ageGateError}>{step1AgeGateMessage}</p>
            ) : null}
            <NegotiationRequestQuoteForm
              formId="OrderPanelRequestQuoteForm"
              onSubmit={gatedOnSubmit}
              finePrintComponent={SubmitFinePrint}
              payoutDetailsWarning={payoutDetailsWarning}
              isOwnListing={isOwnListing}
            />
          </>
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}

        {['boat-rental', 'accessory-rental'].includes(listingType) &&
          listing?.attributes?.publicData?.multiDayListingId ? (
          <div className={css.multiDayLink}>
            <a href={`/l/${listing.attributes.publicData.multiDayListingId}`}>
              📅 Looking for a multi-day rental? →
            </a>
          </div>
        ) : null}
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          categoryLevel1={categoryLevel1}
          serviceMenu={serviceMenu}
          showCurrencyMismatch
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            id={ORDER_PANEL_SUBMIT_BUTTON_ID}
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm || showNegotiationForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
            style={isDockRampDelivery ? { width: '75%', flexShrink: 0, marginLeft: 'auto' } : undefined}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : showNegotiationForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
            ) : showRequestQuoteForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default OrderPanel;
